import { CONTACT, FEE_TERMS, LEGAL_ENTITY, SITE } from '@/constants/site.constants';
import type { LegalDocumentContent } from '@/types/legal.types';

/**
 * Terms of use for the Shoutt consumer app and this website. Written against how
 * the product actually behaves (campus-verified accounts, deposits, manual UPI
 * settlement, coins, moderation). Bracketed values come from site.constants.ts
 * and MUST be replaced with the registered entity details before launch.
 */
export const TERMS_CONTENT: LegalDocumentContent = {
  title: 'Terms & Conditions',
  summary: `These terms govern your use of the ${SITE.name} mobile app and this website. Please read them before you list an item, book a rental, or post a Shoutt.`,
  sections: [
    {
      id: 'agreement',
      heading: '1. Agreement to these terms',
      paragraphs: [
        `${SITE.name} is operated by ${LEGAL_ENTITY.name}, registered at ${LEGAL_ENTITY.address} ("Shoutt", "we", "us"). By creating an account, or by using the app or this website in any way, you agree to these Terms & Conditions and to our Privacy Policy.`,
        'If you do not agree with any part of these terms, do not use the service.',
      ],
    },
    {
      id: 'eligibility',
      heading: '2. Who can use Shoutt',
      bullets: [
        'You must be a currently enrolled student at a college or university we have onboarded, and you must verify a college email address issued by that institution.',
        'You must be at least 18 years old, or have the consent of a parent or legal guardian who accepts these terms on your behalf.',
        'You may hold only one account. Accounts are personal and may not be shared, sold or transferred.',
        'You are responsible for everything that happens through your account, including keeping your device and login secure.',
        'We may ask you to submit a student ID for verification. Providing false, borrowed or forged identity information is grounds for immediate removal.',
      ],
    },
    {
      id: 'what-shoutt-is',
      heading: '3. What Shoutt is, and what it is not',
      paragraphs: [
        `${SITE.name} is a marketplace that lets verified students on the same campus rent items to one another and post requests ("Shoutts") for items they need.`,
        'The rental itself is an agreement between the owner and the borrower. Shoutt is not the owner, seller, lender, borrower, insurer or guarantor of any item listed on the platform. We do not inspect items, take custody of them, or verify that a description is accurate.',
        'We provide the platform, the booking and payment rails, the deposit-handling process and a dispute review service. We do not guarantee that any listing will be booked, that any item will be delivered, or that any user will behave as they promised.',
      ],
    },
    {
      id: 'listings',
      heading: '4. Listings and Shoutts',
      paragraphs: [
        'If you list an item, you confirm that you own it or are permitted to rent it out, that your description and photos are honest and current, and that the item is safe and legal to use.',
      ],
      bullets: [
        'Prohibited items include: weapons, drugs and controlled substances, alcohol and tobacco, prescription medicines, stolen or counterfeit goods, forged documents, adult material, live animals, hazardous chemicals, and anything whose rental is unlawful.',
        'Also prohibited: listing items you do not have, offering academic dishonesty services, or using a listing or Shoutt to advertise a business, a job, or an unrelated service.',
        'You must honour the price, availability and dates you publish. Repeatedly cancelling confirmed bookings may lead to your listings being hidden or your account being restricted.',
        'We may edit, hide or remove any listing, Shoutt, photo or message that breaches these terms, without prior notice.',
      ],
    },
    {
      id: 'bookings',
      heading: '5. Bookings, handover and return',
      bullets: [
        'A booking is confirmed when the borrower has paid and the owner has accepted. From that point, both sides are committed to the dates shown in the app.',
        'The item is handed over in person, on campus. Both sides should inspect it together and record its condition in the app at pickup.',
        'The borrower must use the item reasonably, keep it in their own possession, and not sub-let, modify, pawn or sell it.',
        'The borrower must return the item on the agreed return date, in the condition it was received, allowing for fair wear and tear.',
        'A return is complete only when the owner confirms it in the app. Charged rental days are calculated on the booking dates shown to you before payment.',
      ],
    },
    {
      id: 'payments',
      heading: '6. Payments, fees and taxes',
      paragraphs: [
        'All payments are made in Indian Rupees through the app. Money never passes directly between two students: the borrower pays Shoutt, and Shoutt settles with the owner after the rental completes.',
      ],
      bullets: [
        `The borrower pays the rental amount, a refundable security deposit, a platform commission of ${FEE_TERMS.commissionPct}% of the rental, and GST at ${FEE_TERMS.gstPct}% charged on the fees only, never on the rental or the deposit itself.`,
        `The owner's payout is the rental amount less a settlement fee of ${FEE_TERMS.settlementFeePct}% (plus applicable GST on that fee).`,
        'The full breakdown is shown on screen before you pay. Fee percentages may change; the rates in force are always the ones displayed at the time of your booking.',
        'Payments are currently made over UPI and verified manually by our team. You must submit a genuine payment reference and, where asked, an unedited screenshot of the transaction. Submitting false payment proof is fraud and will result in permanent removal and, where appropriate, a police complaint.',
        'Payouts are made to the bank or UPI details you provide. You are responsible for the accuracy of those details and for any taxes you owe on income you earn through Shoutt.',
      ],
    },
    {
      id: 'deposits',
      heading: '7. Security deposits, damage and disputes',
      bullets: [
        'The security deposit is set by the owner and shown to the borrower before payment. Shoutt holds it for the duration of the booking.',
        'The deposit is released back to the borrower after the owner confirms the return, unless a damage report is raised.',
        'If an item comes back damaged, incomplete, or does not come back at all, the owner must raise a damage report in the app, with photos, within the window shown at return.',
        'Our team reviews the evidence from both sides and decides what amount, if any, is deducted from the deposit and paid to the owner. Our decision on the deposit is final as far as the platform is concerned.',
        'A deposit is a cap on what we can recover for the owner through the platform; it is not a limit on the borrower’s liability. If the loss exceeds the deposit, the owner and borrower must resolve the balance between themselves.',
      ],
    },
    {
      id: 'cancellations',
      heading: '8. Cancellations and refunds',
      bullets: [
        'Either side may cancel before the handover. The cancellation rules and any applicable refund are shown in the app at the time of cancelling.',
        'If the owner cancels a confirmed booking, or the item is not handed over, the borrower is refunded the rental, the deposit and the platform fees.',
        'Refunds are returned to the original payment method or UPI ID and may take several working days to reach you.',
        'Platform fees are non-refundable once a rental has begun, except where the cancellation is the owner’s fault or ours.',
      ],
    },
    {
      id: 'coins',
      heading: '9. Coins',
      bullets: [
        'Coins are a loyalty feature. They are earned for actions such as completing your profile, your first listing, completed rentals, reviews and referrals.',
        'Coins have no cash value. They cannot be bought, sold, transferred between accounts, or exchanged for money.',
        'Coins can be spent inside the app on features such as marking a Shoutt as urgent or reducing platform fees, at the rates shown at the time.',
        'We may change earn rates, spend prices and coin features at any time, and may cancel coins obtained through abuse, fake accounts or manipulated activity.',
      ],
    },
    {
      id: 'trust-and-reviews',
      heading: '10. Trust Score and reviews',
      bullets: [
        'Your Trust Score is calculated from your activity on the platform (completed rentals, on-time returns, ratings and verification steps) and is visible to other students on your campus.',
        'Reviews must be honest and based on a real rental. Do not post reviews in exchange for payment, or use reviews to threaten or pressure the other side.',
        'We may remove reviews that are abusive, fake, off-topic or that reveal someone’s personal information.',
      ],
    },
    {
      id: 'conduct',
      heading: '11. Community rules and moderation',
      paragraphs: [
        'Listings, Shoutts and posts are screened automatically, including by automated content-safety tooling, and anything reported by a student is reviewed by our team.',
      ],
      bullets: [
        'Do not harass, threaten, stalk, impersonate, discriminate against or sexually solicit any other user.',
        'Do not post content that is illegal, hateful, violent, sexually explicit, deceptive, or that infringes someone else’s rights.',
        'Do not scrape the platform, reverse-engineer the app, probe our systems, or attempt to access another user’s account or data.',
        'Do not take rentals off-platform to avoid fees. You lose deposit protection and dispute support, and your account may be removed.',
        'Report anything that breaks these rules using the in-app report option. We may suspend accounts while a report is investigated.',
      ],
    },
    {
      id: 'your-content',
      heading: '12. Your content',
      paragraphs: [
        'You keep ownership of the photos, descriptions, messages and reviews you post. You grant Shoutt a non-exclusive, royalty-free, worldwide licence to host, store, reproduce and display that content for the purpose of operating, promoting and improving the service.',
        'You are responsible for having the rights to everything you upload, and for making sure it does not contain other people’s personal information without their consent.',
      ],
    },
    {
      id: 'ip',
      heading: '13. Our intellectual property',
      paragraphs: [
        `The ${SITE.name} name, logo, wordmark, app, website, design and content are owned by ${LEGAL_ENTITY.name} and protected by intellectual property law. You may not copy, adapt, or use them without our written permission.`,
      ],
    },
    {
      id: 'suspension',
      heading: '14. Suspension and termination',
      bullets: [
        'You may stop using Shoutt and request deletion of your account at any time. Deletion does not cancel obligations from a booking that is still running.',
        'We may suspend or terminate your account, with or without notice, if you breach these terms, if we are required to by law, or if we reasonably believe your activity puts other students or the platform at risk.',
        'On termination, pending payouts owed to you will still be settled, less any amounts properly deducted under a dispute.',
      ],
    },
    {
      id: 'disclaimers',
      heading: '15. Disclaimers and limitation of liability',
      paragraphs: [
        'The service is provided "as is" and "as available". To the maximum extent permitted by law, we disclaim all warranties, express or implied, including as to the quality, safety, legality or fitness of any item listed by a user, and as to uninterrupted or error-free operation of the app.',
        'To the maximum extent permitted by law, Shoutt is not liable for indirect, incidental, special or consequential loss, loss of profit, loss of data, or for injury, loss or damage arising out of a rental between two users. Our total liability to you for any claim is limited to the platform fees you paid to us in respect of the booking that gave rise to the claim.',
        'Nothing in these terms excludes liability that cannot be excluded under Indian law.',
      ],
    },
    {
      id: 'indemnity',
      heading: '16. Indemnity',
      paragraphs: [
        'You agree to indemnify and hold Shoutt harmless against any claim, loss, liability or expense arising from your use of the service, your listings or rentals, your content, or your breach of these terms or of any law.',
      ],
    },
    {
      id: 'changes',
      heading: '17. Changes to the service and these terms',
      paragraphs: [
        'We may update these terms as the product and the law change. When we do, we will update the "Last updated" date on this page and, for material changes, notify you in the app. Continuing to use Shoutt after a change means you accept the updated terms.',
        'We may also add, change or withdraw features, campuses or the service itself.',
      ],
    },
    {
      id: 'law',
      heading: '18. Governing law and disputes',
      paragraphs: [
        `These terms are governed by the laws of India. Any dispute between you and ${LEGAL_ENTITY.name} will be subject to the exclusive jurisdiction of the courts at ${LEGAL_ENTITY.jurisdiction}.`,
        'Before starting any formal proceedings, please write to us. Most issues are resolved far more quickly that way.',
      ],
    },
    {
      id: 'contact',
      heading: '19. Contact and grievances',
      paragraphs: [
        `Questions about these terms: ${CONTACT.email}.`,
        `Grievance Officer (as required under Indian law): ${LEGAL_ENTITY.grievanceOfficer}, ${LEGAL_ENTITY.name}, ${LEGAL_ENTITY.address}. Email: ${CONTACT.grievanceEmail}. We aim to acknowledge every grievance within 24 hours and resolve it within 15 days.`,
      ],
    },
  ],
};
